import PropTypes from 'prop-types';
import React, { useState, useEffect, Fragment } from 'react';
import classNames from 'classnames';
import { Record } from 'immutable';

import { capitalize } from '@xanadu/ui-shared/utils/string';

import IconLock from '../../../../../images/icons/misc/icon_lock_white.svg';
import IconUnlock from '../../../../../images/icons/misc/icon_unlock_white.svg';
import IconCheck from '../../../../../images/icons/misc/check.svg';
import IconAlert from '../../../../../images/icons/misc/icon-alert.svg';
import IconChevronRight from '../../../../../images/icons/misc/icon-d-right.svg';

import style from './OfferEdit.module.scss';
import Button from '@xanadu/ui-shared/components/widgets/Button';
import Spinner from '@xanadu/ui-shared/components/widgets/Spinner';

const OfferEdit = ({
  offer,
  processingState,
  editModal,
  betData,
  confirmed,
  offerIndex,
  currency,
  updateBack,
  updateLay,
  oddsLadder,
  updateOdds,
  updateOddsLockStatus,
  oddsLocked,
  oddsType,
  refOdds,
  handleSubmit,
  reset,
  stringIsValidNumber,
  refInputStake,
  setEditModal,
  setUnmatchedData,
  showBetConfirmation,
  setConfirmed,
  lastEditedInput,
  isUnsubmittedType,
  isBetBuilderBet = false,
  isMobile,
  countdown,
  eventInRunning,
  betBuilderWithErrors,
  prevBetBuilderOdds,
  unmatchedBetUseBonus = false
}) => {
  const minorIcon = '/assets/images/betslip/icon-minus.svg';
  const plusIcon = '/assets/images/betslip/icon-plus.svg';

  const [addWheelListener, setAddWheelListener] = useState(false);

  const isSubmitting = processingState === 'submitting';

  const oddsEditingLocked = (offer.status === 'open' && (offer.useBonus || unmatchedBetUseBonus))
    || confirmed
    || oddsLocked
    || isSubmitting
    || isBetBuilderBet;

  const { adjustedOdds, back } = betData;

  const inputsDisabled = offer.useBonus || confirmed || isSubmitting || unmatchedBetUseBonus;

  const disabledSubmitButton = (adjustedOdds === '' || back === '' || back === undefined)
    || confirmed
    || countdown > 0
    || processingState === 'submitting'
    || betBuilderWithErrors;

  const handleDecreaseControl = () => {
    const newOddsLadder = oddsLadder(oddsType);
    const valueNoRounded = refOdds.current.dataset.valueNoRounded || 1;
    updateOdds(newOddsLadder.quantiseDown(valueNoRounded, true));
  };

  const handleIncreaseControl = () => {
    const newOddsLadder = oddsLadder(oddsType);
    const valueNoRounded = refOdds.current.dataset.valueNoRounded || 1;
    updateOdds(newOddsLadder.quantiseUp(valueNoRounded, true));
  };

  const handleKeyUp = (e) => {
    if (e.key === 'Enter' && (offer.edited || offer.status === 'unsubmitted')) {
      if (showBetConfirmation) {
        setConfirmed(true);
      } else {
        handleSubmit();
      }
    }
    if (e.key === 'Enter' && !offer.edited) {
      setEditModal(false);
      setUnmatchedData(null);
    }
    if (e.key === 'Escape') reset();
  };

  const handleInput = (e) => {
    const value = e.target ? e.target.value : '';
    const validInput = stringIsValidNumber(value);

    if (!validInput) {
      e.preventDefault();
      return;
    }

    switch (e.target.name) {
      case 'oddsInput':
        updateOdds(value);
        return;
      case 'backInput':
        updateBack(value);
        return;
      case 'layInput':
        updateLay(value);
        return;
      default:
        // eslint-disable-next-line no-console
        console.warn('Unrecognized input source and/or event');
    }
  };

  const stepOdds = (e) => {
    const up = (e.key === 'ArrowUp' || e.deltaY < 0);
    const down = (e.key === 'ArrowDown' || e.deltaY > 0);
    const value = e.target.value;

    const newOddsLadder = oddsLadder(oddsType);
    if (up || down) {
      e.preventDefault();
    }

    if (up) {
      updateOdds(newOddsLadder.quantiseUp(value, true));
    }

    if (down) {
      updateOdds(newOddsLadder.quantiseDown(value, true));
    }
  };

  const handleMouseWheel = (event) => {
    event.preventDefault();
    const newOddsLadder = oddsLadder(oddsType);

    const odds = refOdds.current && refOdds.current.value;

    if (event.deltaY > 0) {
      updateOdds(newOddsLadder.quantiseUp(odds, true));
    }

    if (event.deltaY < 0) {
      updateOdds(newOddsLadder.quantiseDown(odds, true));
    }
  };

  const onSubmit = () => {
    if (editModal && !offer.edited) {
      setEditModal(false);
      setUnmatchedData(null);

      return;
    }

    if (isUnsubmittedType && !confirmed && showBetConfirmation && countdown === 0) {
      setConfirmed(true);
      return;
    }

    handleSubmit();
  };

  // we need to change this logic to get the wheel working without focus
  useEffect(() => {
    if (addWheelListener) {
      refOdds.current.addEventListener('wheel', handleMouseWheel);
    }

    return () => {
      if (refOdds.current) {
        refOdds.current.removeEventListener('wheel', handleMouseWheel);
      }
    };
  }, [addWheelListener, refOdds]);

  const showOddsLocker = isUnsubmittedType && !isBetBuilderBet && !isMobile;

  const showOddsControls = !isBetBuilderBet;

  const oddsClassNames = classNames({
    [style.odds]: true,
    [style['odds--disabled']]: offer.status === 'open' && (offer.useBonus || unmatchedBetUseBonus),
    [style['odds--edited']]: offer.edited,
    [style[`odds--${offer.side}`]]: true,
    [style['odds--withOddsLocker']]: showOddsLocker
  });

  const backInputClassNames = classNames({
    [style.betslipInputWithoutBtns]: true,
    [style[`betslipInputWithoutBtns--${offer.side}--${currency}`]]: true,
    [style['betslipInputWithoutBtns--disabled']]: offer.useBonus || unmatchedBetUseBonus,
  });

  const layInputClassNames = classNames({
    [style.betslipInputWithoutBtns]: true,
    [style[`betslipInputWithoutBtns--${offer.side}--${currency}`]]: true,
    [style['betslipInputWithoutBtns--editedBackground']]: offer.edited,
  });

  const labelClassNames = classNames({
    [style.label]: true,
    [style[`label--${offer.side}`]]: true,
  });

  const backersStakeClassNames = classNames({
    [style.backersStake]: true,
    [style['backersStake--edited']]: offer.edited,
    [style[`backersStake--disabled--${offer.side}`]]: offer.useBonus || unmatchedBetUseBonus,
    [style[`backersStake--${offer.side}`]]: true,
    [style['backersStake--showEditBadge']]: lastEditedInput === 'back',
  });

  const layersStakeClassNames = classNames({
    [style.layersStake]: true,
    [style['layersStake--edited']]: offer.edited,
    [style[`layersStake--disabled--${offer.side}`]]: offer.useBonus || unmatchedBetUseBonus,
    [style[`layersStake--${offer.side}`]]: true,
    [style['layersStake--showEditBadge']]: lastEditedInput === 'lay',
  });

  const betslipInputClassNames = classNames({
    [style.betslipInput]: true,
    [style['betslipInput--edited']]: offer.edited,
    [style['betslipInput--oddsLocked']]: oddsLocked,
  });

  const placeBetButtonClass = classNames({
    [style.placeBetButton]: true,
    [style['placeBetButton--disabled']]: disabledSubmitButton,
  });

  let labelInputOdds = offer.side;
  let labelInputStake = 'stake';
  let labelInputPL = 'profit';

  if (offer.side === 'back' || isBetBuilderBet) {
    labelInputOdds = isBetBuilderBet ? 'odds' : 'back';
    labelInputStake = 'stake';
    labelInputPL = 'profit';
  } else if (offer.side === 'lay') {
    labelInputOdds = 'lay';
    labelInputStake = 'stake';
    labelInputPL = 'liability';
  }

  return (
    <div>
      {((offer.status !== 'open' && offer.status !== 'matched' && offer.status !== 'edited') || editModal) && (
        <div className={style.containerOfferProperties}>
          <div className={oddsClassNames}>
            <span
              className={`${offer.edited ? style.hint : ''}`}
              aria-label={`Current: ${offer.remote && offer.remote.odds}`}
              disabled={offer.status === 'open' && (offer.useBonus || unmatchedBetUseBonus)}
            >
              <span className={labelClassNames}>
                {labelInputOdds}
              </span>
              {prevBetBuilderOdds && (
                <span className={style.prevOddsOverlay}>
                  <span>{prevBetBuilderOdds}</span>
                  <IconChevronRight />
                  <span>{betData.odds}</span>
                </span>
              )}
              <input
                type="text"
                inputMode="decimal"
                name="oddsInput"
                autoComplete="off"
                ref={refOdds}
                className={betslipInputClassNames}
                onBlur={e => {
                  setAddWheelListener(false); updateOdds(e.target.value, { quantise: true });
                }}
                onKeyDown={stepOdds}
                // temp disable due to issue
                // onKeyUp={e => handleKeyUp(e)}
                onFocus={() => setAddWheelListener(true)}
                onChange={handleInput}
                data-value-no-rounded={betData.odds}
                value={betData.adjustedOdds || ''}
                data-hook={`betslip-back-${offerIndex}`}
                disabled={oddsEditingLocked}
              />
            </span>
            {showOddsLocker && (
              <button
                tabIndex="-1"
                data-hook="betslip-odds-locker"
                aria-label="Locks the odds to follow the market price"
                className={`hint hint--top-right hint--medium ${style.oddsLocker}`}
                onClick={updateOddsLockStatus}
                disabled={isMobile || confirmed || isSubmitting}
              >
                {oddsLocked ? <IconLock /> : <IconUnlock />}
              </button>
            )}
            {showOddsControls && (
              <Fragment>
                <button
                  tabIndex="-1"
                  className={style.controlDecreaseOdds}
                  onClick={handleDecreaseControl}
                  data-hook="betslip-deduct"
                  disabled={oddsEditingLocked}
                >
                  <img src={minorIcon} alt="minus-icon" />
                </button>
                <button
                  tabIndex="-1"
                  className={style.controlIncreaseOdds}
                  onClick={handleIncreaseControl}
                  data-hook="betslip-add"
                  disabled={oddsEditingLocked}
                >
                  <img src={plusIcon} alt="plus-icon" />
                </button>
              </Fragment>
            )}
          </div>
          <div className={backersStakeClassNames}>
            <span
              className={`${offer.edited ? style.hint : ''}`}
              aria-label={`Current: ${offer.remote && offer.remote.backersStake}`}
            >
              <span className={labelClassNames}>
                {labelInputStake}
              </span>
              <input
                type="text"
                inputMode="decimal"
                name="backInput"
                autoComplete="off"
                ref={refInputStake}
                className={backInputClassNames}
                // temp disable due to issue
                // onKeyUp={e => handleKeyUp(e)}
                onFocus={e => e.target.select()}
                onChange={handleInput}
                value={betData.back || ''}
                data-hook={`betslip-stake-${offerIndex}`}
                disabled={inputsDisabled}
              />
              <span
                aria-label={`${capitalize(labelInputStake)} has priority`}
                className={`hint hint--top-left hint--bounce ${style.editingInputBadge}`}
              >
                <span />
              </span>
            </span>
          </div>
          <div className={layersStakeClassNames}>
            <span
              className={`${offer.edited ? style.hint : ''}`}
              aria-label={`Current: ${offer.remote && offer.remote.layersStake}`}
            >
              <span className={labelClassNames}>
                {labelInputPL}
              </span>
              <input
                type="text"
                inputMode="decimal"
                name="layInput"
                autoComplete="off"
                className={layInputClassNames}
                // temp disable due to issue
                // onKeyUp={e => handleKeyUp(e)}
                onFocus={e => e.target.select()}
                onChange={handleInput}
                value={betData.lay || ''}
                data-hook={`betslip-profit-${offerIndex}`}
                disabled={inputsDisabled || isBetBuilderBet}
              />
              <span
                aria-label={`${capitalize(labelInputPL)} has priority`}
                className={`hint hint--top-left hint--bounce ${style.editingInputBadge}`}
              >
                <span />
              </span>
            </span>
          </div>
          <div className={style.firstAction}>
            <Button
              sizeType="extra-small"
              buttonType="primary"
              title="Place Bet"
              loading={((processingState === 'submitting' && !eventInRunning)) || confirmed}
              disabled={disabledSubmitButton}
              className={placeBetButtonClass}
              onClick={onSubmit}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  onSubmit();
                }
              }}
              dataHook={`betslipcheckmark-${offerIndex}`}
            >
              {processingState === 'submitting' ? (
                <Fragment>
                  <Spinner theme="light" size="medium" />
                  {eventInRunning && countdown > 0 && (
                    <span className={style.countdown}>{countdown}</span>
                  )}
                </Fragment>
              ) : (
                <Fragment>
                  {betBuilderWithErrors ? (
                    <IconAlert />
                  ) : (
                    <IconCheck />
                  )}
                </Fragment>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

OfferEdit.propTypes = {
  offer: PropTypes.instanceOf(Record).isRequired,
  processingState: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  offerIndex: PropTypes.number,
  betData: PropTypes.instanceOf(Object).isRequired,
  editModal: PropTypes.bool.isRequired,
  confirmed: PropTypes.bool.isRequired,
  updateBack: PropTypes.func.isRequired,
  updateLay: PropTypes.func.isRequired,
  oddsLadder: PropTypes.func.isRequired,
  updateOdds: PropTypes.func.isRequired,
  setEditModal: PropTypes.func.isRequired,
  setUnmatchedData: PropTypes.func.isRequired,
  showBetConfirmation: PropTypes.bool.isRequired,
  setConfirmed: PropTypes.func.isRequired,
  oddsType: PropTypes.string,
  refOdds: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ]),
  refInputStake: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ]),
  handleSubmit: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  stringIsValidNumber: PropTypes.func.isRequired,
  updateOddsLockStatus: PropTypes.func,
  oddsLocked: PropTypes.bool,
  countdown: PropTypes.number,
  eventInRunning: PropTypes.bool.isRequired,
  isUnsubmittedType: PropTypes.bool.isRequired,
  isBetBuilderBet: PropTypes.bool,
  isMobile: PropTypes.bool.isRequired,
  lastEditedInput: PropTypes.oneOf(['back', 'lay']),
  betBuilderWithErrors: PropTypes.bool,
  unmatchedBetUseBonus: PropTypes.bool,
  prevBetBuilderOdds: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default OfferEdit;
